import { useEffect, useState } from "react";
import { Drawer } from "../common/Modal";
import Button from "../common/Button";
import FormField from "../common/FormField";
import { PRIORITIES, GOAL_SOURCES, scaleTextFromUnit, unitFromScaleText } from "../../utils/constants";
import { validateGoal, hasErrors } from "../../utils/validators";
import { goalLimitMessage } from "../../utils/constants";
import Banner from "../common/Banner";
import DraftNotice from "../common/DraftNotice";
import useFormDraft from "../../hooks/useFormDraft";
import Select from "../common/Select"; // [NEW] alphabetical dropdown (replaces <select>)

// [UPDATED] The goal form no longer asks for a target or measurement scale. Every goal uses these defaults.
const DEFAULT_TARGET = "100";
const DEFAULT_SCALE = "1 to 5";

const EMPTY = {
  title: "", description: "", categoryId: "", priority: "Medium", weightage: "",
  baseline: "", target: DEFAULT_TARGET, unit: unitFromScaleText(DEFAULT_SCALE), scale: DEFAULT_SCALE, dueDate: "", employeeComments: "", evidence: null,
  source: GOAL_SOURCES.EMPLOYEE,
};

export default function GoalFormDrawer({ open, goal, initial, categories = [], remaining = 100, cycle, onClose, onSave, title, subtitle, submitLabel, mode = "employee", employeeName, goalUsage = null }) {
  const isManager = mode === "manager";
  const [form, setForm] = useState(EMPTY);
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      // [UPDATED] The measurement scale is read from the saved unit ("Scale (0-5)"); 0 to 5 when it has none.
      const scaleOf = (u) => ({ scale: scaleTextFromUnit(u) });
      if (goal) setForm({ ...EMPTY, ...goal, ...scaleOf(goal.unit), weightage: String(goal.weightage), baseline: goal.baseline ?? "", employeeComments: goal.notes || "" });
      else if (initial) setForm({ ...EMPTY, ...initial, ...scaleOf(initial.unit), weightage: String(initial.weightage ?? ""), target: String(initial.target ?? DEFAULT_TARGET), baseline: initial.baseline ?? "" });
      // [UPDATED] Requirement 5: a new goal is always the employee's own goal (managers do not assign goals).
      else setForm({ ...EMPTY, source: GOAL_SOURCES.EMPLOYEE, weightage: remaining > 0 ? String(remaining) : "" });
      setErrors({});
    }
  }, [open, goal, initial, remaining]);

  // [NEW] Unsaved typing is kept in this browser and restored when the same goal form is opened again.
  const draftKey = goal ? `goal:${goal.id}` : `goal:new:${mode}:${employeeName || "me"}:${cycle?.id || ""}:${initial?.id || initial?.title || ""}`;
  const draft = useFormDraft(draftKey, form, setForm, { active: open, omit: ["evidence"] });

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const isNew = !goal;
  const limitHit = isNew && Boolean(goalUsage?.limitReached);

  const submit = async (e) => {
    e?.preventDefault();
    if (limitHit) {
      setErrors({ limit: goalLimitMessage(goalUsage.yearLabel) });
      return;
    }
    // Hidden fields always carry a valid value, so an old draft or an empty suggestion cannot block saving.
    const values = { ...form, target: String(form.target || DEFAULT_TARGET), scale: form.scale || DEFAULT_SCALE };
    const found = validateGoal(values);
    const pct = Number(form.weightage);
    if (!found.weightage && remaining >= 0 && remaining < 100 && pct > remaining) {
      found.weightage = remaining > 0 ? `Only ${remaining}% goal percentage is left on this sheet.` : "The sheet already totals 100%. Reduce another goal's percentage first.";
    }
    if (cycle && form.dueDate && (form.dueDate < cycle.startDate || form.dueDate > cycle.endDate)) {
      found.dueDate = `Pick a date inside ${cycle.name}.`;
    }
    setErrors(found);
    if (hasErrors(found)) return;
    setSaving(true);
    const okSaved = await onSave({ ...values, unit: unitFromScaleText(values.scale) });
    setSaving(false);
    if (okSaved) { draft.clear(); onClose(); }
  };

  return (
    <Drawer
      open={open}
      title={title || (isManager ? `Edit goal${employeeName ? ` of ${employeeName}` : ""}` : (goal ? "Edit goal" : "Add a goal"))}
      subtitle={subtitle || (isManager ? `You are correcting the employee's own goal while reviewing it. ${Math.max(0, remaining)}% goal percentage is still free on their sheet.` : goal ? "Changes stay in draft until you submit the sheet." : initial ? `Pre-filled from your role template. You have ${Math.max(0, remaining)}% goal percentage left to assign.` : `You have ${Math.max(0, remaining)}% goal percentage left to assign.`)}
      onClose={saving ? undefined : onClose}
      size="lg"
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button onClick={submit} loading={saving} disabled={limitHit}>{submitLabel || (goal ? "Save changes" : "Add goal")}</Button>
        </>
      }
    >
      {isNew && goalUsage && (
        limitHit
          ? <Banner tone="danger" title="Goal limit reached">{goalLimitMessage(goalUsage.yearLabel)}</Banner>
          : <p className="form-hint">This will be goal {goalUsage.used + 1} of {goalUsage.max} for {goalUsage.yearLabel}.</p>
      )}
      {errors.limit && !limitHit && <Banner tone="danger" title="Goal limit reached">{errors.limit}</Banner>}
      <DraftNotice draft={draft} />
      <form className="form-grid" onSubmit={submit} noValidate>
        <FormField label="Goal title" required error={errors.title} className="span-2">
          <input value={form.title} onChange={set("title")} maxLength={120} placeholder="For example, Reduce ticket resolution time" />
        </FormField>
        <FormField label="Description" required error={errors.description} hint="What does success look like?" className="span-2">
          <textarea rows={3} value={form.description} onChange={set("description")} />
        </FormField>
        <FormField label="Category" required error={errors.categoryId}>
          <Select value={form.categoryId} onChange={set("categoryId")}>
            <option value="">Choose a category</option>
            {categories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </Select>
        </FormField>
        <FormField label="Priority" required error={errors.priority}>
          <div className="segmented" role="radiogroup" aria-label="Priority">
            {PRIORITIES.map((p) => (
              <button key={p} type="button" role="radio" aria-checked={form.priority === p} className={form.priority === p ? "is-active" : ""} onClick={() => setForm((f) => ({ ...f, priority: p }))}>
                {p}
              </button>
            ))}
          </div>
        </FormField>
        <FormField
          label="Goal percentage (%)"
          required
          error={errors.weightage}
          hint={`Share of the goal score this goal carries (1–100). ${isManager ? "All goals on the employee's sheet" : "All your goals"} must total exactly 100%${remaining < 100 ? `; ${Math.max(0, remaining)}% is free` : ""}.`}
        >
          <input type="number" min="1" max={remaining > 0 && remaining < 100 ? remaining : 100} step="1" value={form.weightage} onChange={set("weightage")} aria-label="Goal percentage" />
        </FormField>
        <FormField label="Due date" required error={errors.dueDate}>
          <input type="date" value={form.dueDate} min={cycle?.startDate} max={cycle?.endDate} onChange={set("dueDate")} />
        </FormField>
        {/* [UPDATED] No target, measurement scale, unit, baseline or source fields. Target 100 and scale 1 to 5 are set automatically. */}
        <p className="form-hint span-2">Supporting documents can be attached with a progress update once the goal is approved.</p>
        <FormField label={isManager ? "Reason / note for the employee" : "Your notes"} hint="Saved with the goal and shown to your manager." className="span-2">
          <textarea rows={2} value={form.employeeComments} onChange={set("employeeComments")} placeholder={isManager ? "Why you changed this goal" : "Anything your manager should know"} />
        </FormField>
      </form>
    </Drawer>
  );
}